import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { useReviewApi } from '@/components/admin/sessions/review/review-context';
import { DISABLED_INERT } from '@/lib/disabled-inert';
import { type PassageTarget, placementPreview } from '@/lib/review-gestures';
import { shortPassage } from '@/lib/review-transcript';
import { cn } from '@/lib/utils';

/**
 * The small list a selection of a reply opens (relecture.md §5.2): « Ranger
 * « … » (tour 2) dans : », one button per case - the keyboard's way to range,
 * beside the drag.
 *
 * Under « un passage, un seul objectif » it warns when the passage is already
 * ranged elsewhere, and says which cases it would put « avec doute » (§5.4). A
 * case that already holds this very passage is shown and closed: the gesture
 * would change nothing.
 */

export interface ReviewRangeListProps {
  readonly target: PassageTarget;
  readonly onPick: (objectiveId: string) => void;
}

export function ReviewRangeList({ target, onPick }: ReviewRangeListProps) {
  const { grid, review, busy } = useReviewApi();
  if (grid === null) return null;
  const preview = placementPreview(target, review.extracts, grid);

  return (
    <>
      <p className="text-gray-600">
        Ranger <q className="text-gray-900">{shortPassage(target.text, 70)}</q> (tour {target.turn})
        dans :
      </p>
      {preview.warns ? (
        <p
          data-testid={REVIEW_TEST_IDS.optionWarning}
          className="rounded-md bg-jaune-tint px-2 py-1.5 text-jaune-deep text-caption"
        >
          Déjà rangé en {preview.sharedWith.map((one) => `${one.number}. ${one.name}`).join(', ')}.
          Un passage ne sert qu’un objectif : le ranger aussi ailleurs met un « ? » sur les deux
          placements.
        </p>
      ) : null}
      <div className="flex flex-col gap-0.5">
        {preview.options.map((option) => (
          <button
            key={option.objectiveId}
            type="button"
            data-testid={REVIEW_TEST_IDS.option}
            data-objective={option.objectiveId}
            data-doubt={option.withDoubt}
            disabled={busy || option.taken}
            onClick={() => onPick(option.objectiveId)}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-bleu-nuit-tint focus-visible:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit',
              (busy || option.taken) && `cursor-not-allowed ${DISABLED_INERT}`,
            )}
          >
            <span
              aria-hidden="true"
              className="inline-flex size-[22px] shrink-0 items-center justify-center rounded-full bg-bleu-nuit font-bold text-white text-caption"
            >
              {option.number}
            </span>
            <span>
              {option.name}
              {option.taken ? (
                <span className="text-gray-600 text-caption"> · déjà rangé ici</span>
              ) : option.withDoubt ? (
                <span className="font-semibold text-gray-600 text-caption"> · avec doute</span>
              ) : null}
            </span>
          </button>
        ))}
      </div>
      <p className="text-gray-500 text-caption">
        Ou glissez la sélection sur une case. Échap pour fermer.
      </p>
    </>
  );
}
